import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Loader2,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from "@sportscanner/ui";
import { apiDelete, apiGet, apiPost, apiPut, getApiErrorCode } from "@/lib/api";
import ConfirmDialog from "@/components/confirm-dialog";
import {
  COMPETITION_STATUSES,
  type AdminParticipant,
  type CompetitionDetailResponse,
  type Fixture,
  type SerializedCompetition,
} from "./types";

interface Props {
  clubId: string;
  competitionId: string;
  sports: { id: string; name: string }[];
  onBack: () => void;
}

export default function CompetitionDetail({
  clubId,
  competitionId,
  onBack,
}: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const key = ["admin", "competition", clubId, competitionId];
  const query = useQuery({
    queryKey: key,
    queryFn: () =>
      apiGet<CompetitionDetailResponse>(
        `/api/admin/clubs/${clubId}/competitions/${competitionId}`,
      ),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: key });

  const statusMutation = useMutation({
    mutationFn: (status: string) =>
      apiPut(
        `/api/admin/clubs/${clubId}/competitions/${competitionId}/status`,
        { status },
      ),
    onSuccess: async () => {
      toast.success(t("common.saved"));
      await invalidate();
    },
    onError: (err) => toast.error(t(`errors.api.${getApiErrorCode(err)}`)),
  });

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }
  if (query.isError || !query.data?.competition) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{t("errors.api.INTERNAL_ERROR")}</AlertDescription>
      </Alert>
    );
  }

  const { competition, participants } = query.data;

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-800"
      >
        <ArrowLeft className="w-4 h-4" />
        {t("competitions.backToList")}
      </button>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-bold text-neutral-800">
            {competition.name}
          </h2>
          <p className="text-sm text-neutral-500 mt-0.5">
            {t(`competitions.format.${competition.format}`)}
            {competition.season ? ` · ${competition.season}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-neutral-500">
            {t("competitions.statusLabel")}
          </Label>
          <Select
            value={competition.status}
            onValueChange={(v) => statusMutation.mutate(v)}
          >
            <SelectTrigger className="h-9 rounded-lg border-neutral-200 w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COMPETITION_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`competitions.status.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="registrations">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="registrations">
            {t("competitions.tabs.registrations")}
          </TabsTrigger>
          <TabsTrigger value="structure">
            {t("competitions.tabs.structure")}
          </TabsTrigger>
          <TabsTrigger value="fixtures">
            {t("competitions.tabs.fixtures")}
          </TabsTrigger>
          <TabsTrigger value="standings">
            {t("competitions.tabs.standings")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="registrations" className="mt-4">
          <RegistrationsTab
            clubId={clubId}
            competitionId={competitionId}
            competition={competition}
            participants={participants}
            onChange={invalidate}
          />
        </TabsContent>
        <TabsContent value="structure" className="mt-4">
          <StructureTab
            clubId={clubId}
            competitionId={competitionId}
            competition={competition}
            participants={participants}
            onChange={invalidate}
          />
        </TabsContent>
        <TabsContent value="fixtures" className="mt-4">
          <FixturesTab
            clubId={clubId}
            competitionId={competitionId}
            competition={competition}
            onChange={invalidate}
          />
        </TabsContent>
        <TabsContent value="standings" className="mt-4">
          <StandingsTab competition={competition} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Registrations ─────────────────────────────────────────────────────────────

function RegistrationsTab({
  clubId,
  competitionId,
  competition,
  participants,
  onChange,
}: {
  clubId: string;
  competitionId: string;
  competition: SerializedCompetition;
  participants: AdminParticipant[];
  onChange: () => void;
}) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const base = `/api/admin/clubs/${clubId}/competitions/${competitionId}`;

  const pending = participants.filter((p) => p.status === "PENDING");
  const confirmed = participants.filter((p) => p.status === "CONFIRMED");
  const other = participants.filter(
    (p) => p.status === "WITHDRAWN" || p.status === "REJECTED",
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
          <Plus className="w-4 h-4 mr-1.5" />
          {t("competitions.addWalkin")}
        </Button>
      </div>

      {pending.length > 0 && (
        <Section title={t("competitions.pendingTitle")}>
          {pending.map((p) => (
            <ParticipantRow
              key={p.id}
              base={base}
              competition={competition}
              participant={p}
              onChange={onChange}
            />
          ))}
        </Section>
      )}

      <Section
        title={t("competitions.confirmedTitle", { count: confirmed.length })}
      >
        {confirmed.length === 0 ? (
          <p className="text-sm text-neutral-400 px-1 py-2">
            {t("competitions.noConfirmed")}
          </p>
        ) : (
          confirmed.map((p) => (
            <ParticipantRow
              key={p.id}
              base={base}
              competition={competition}
              participant={p}
              onChange={onChange}
            />
          ))
        )}
      </Section>

      {other.length > 0 && (
        <Section title={t("competitions.inactiveTitle")}>
          {other.map((p) => (
            <ParticipantRow
              key={p.id}
              base={base}
              competition={competition}
              participant={p}
              onChange={onChange}
            />
          ))}
        </Section>
      )}

      {adding && (
        <AddParticipantDialog
          base={base}
          competition={competition}
          onClose={() => setAdding(false)}
          onAdded={onChange}
        />
      )}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-2">
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function ParticipantRow({
  base,
  competition,
  participant,
  onChange,
}: {
  base: string;
  competition: SerializedCompetition;
  participant: AdminParticipant;
  onChange: () => void;
}) {
  const { t } = useTranslation();
  const [division, setDivision] = useState(participant.divisionLabel ?? "");
  const [group, setGroup] = useState(participant.groupName ?? "");
  const [seed, setSeed] = useState(
    participant.seed != null ? String(participant.seed) : "",
  );

  const save = useMutation({
    mutationFn: () =>
      apiPut(`${base}/participants/${participant.id}`, {
        divisionLabel: division || null,
        groupName: group || null,
        seed: seed ? Number(seed) : null,
      }),
    onSuccess: async () => {
      toast.success(t("common.saved"));
      onChange();
    },
    onError: (err) => toast.error(t(`errors.api.${getApiErrorCode(err)}`)),
  });

  const decide = useMutation({
    mutationFn: (status: string) =>
      apiPut(`${base}/participants/${participant.id}/decide`, { status }),
    onSuccess: async () => onChange(),
    onError: (err) => toast.error(t(`errors.api.${getApiErrorCode(err)}`)),
  });

  const remove = useMutation({
    mutationFn: () => apiDelete(`${base}/participants/${participant.id}`),
    onSuccess: async () => onChange(),
    onError: (err) => toast.error(t(`errors.api.${getApiErrorCode(err)}`)),
  });

  const dirty =
    (division || null) !== (participant.divisionLabel ?? null) ||
    (group || null) !== (participant.groupName ?? null) ||
    (seed ? Number(seed) : null) !== (participant.seed ?? null);

  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-neutral-800 text-sm">
          {participant.name}
        </span>
        {participant.handle && (
          <span className="text-xs text-neutral-400">
            @{participant.handle}
          </span>
        )}
        {!participant.playerUserId && (
          <Badge variant="secondary" className="text-[10px]">
            {t("competitions.walkin")}
          </Badge>
        )}
        {participant.phone && (
          <span className="text-xs text-neutral-400">{participant.phone}</span>
        )}
      </div>
      {participant.note && (
        <p className="text-xs text-neutral-500 mt-1">{participant.note}</p>
      )}

      <div className="flex items-end gap-2 mt-2 flex-wrap">
        {competition.divisions.length > 0 && (
          <div className="space-y-1">
            <Label className="text-[11px] text-neutral-500">
              {t("competitions.classLabel")}
            </Label>
            <Select
              value={division || "__none__"}
              onValueChange={(v) => setDivision(v === "__none__" ? "" : v)}
            >
              <SelectTrigger className="h-8 w-24 rounded-lg border-neutral-200 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {competition.divisions.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-[11px] text-neutral-500">
            {t("competitions.groupLabel")}
          </Label>
          <Input
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            className="h-8 w-20 rounded-lg border-neutral-200 text-xs"
            placeholder="A"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px] text-neutral-500">
            {t("competitions.seedLabel")}
          </Label>
          <Input
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            type="number"
            min={1}
            className="h-8 w-16 rounded-lg border-neutral-200 text-xs"
          />
        </div>
        <div className="flex-1" />
        {dirty && (
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {t("common.save")}
          </Button>
        )}
        {participant.status === "PENDING" && (
          <Button
            size="sm"
            className="h-8 bg-primary-600 hover:bg-primary-700 text-white"
            disabled={decide.isPending}
            onClick={() => decide.mutate("CONFIRMED")}
          >
            <Check className="w-4 h-4" />
          </Button>
        )}
        {participant.status !== "REJECTED" && (
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-neutral-500"
            disabled={decide.isPending}
            onClick={() => decide.mutate("REJECTED")}
            aria-label={t("competitions.reject")}
          >
            <X className="w-4 h-4" />
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="h-8 text-red-600 hover:bg-red-50"
          disabled={remove.isPending}
          onClick={() => remove.mutate()}
          aria-label={t("common.delete")}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function AddParticipantDialog({
  base,
  competition,
  onClose,
  onAdded,
}: {
  base: string;
  competition: SerializedCompetition;
  onClose: () => void;
  onAdded: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [division, setDivision] = useState("");
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      apiPost(`${base}/participants`, {
        name: name.trim(),
        phone: phone.trim() || null,
        divisionLabel: division || null,
        status: "CONFIRMED",
      }),
    onSuccess: () => {
      toast.success(t("common.saved"));
      onAdded();
      onClose();
    },
    onError: (err) => setErrorCode(getApiErrorCode(err)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("competitions.addWalkinTitle")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {errorCode && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t(`errors.api.${errorCode}`)}
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-1.5">
            <Label className="text-sm text-neutral-600">
              {t("competitions.playerNameLabel")}
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-lg border-neutral-200"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-neutral-600">
              {t("competitions.phoneLabel")}
            </Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="rounded-lg border-neutral-200"
            />
          </div>
          {competition.divisions.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-sm text-neutral-600">
                {t("competitions.classLabel")}
              </Label>
              <Select
                value={division || "__none__"}
                onValueChange={(v) => setDivision(v === "__none__" ? "" : v)}
              >
                <SelectTrigger className="rounded-lg border-neutral-200 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {competition.divisions.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || !name.trim()}
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              {mutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("common.add")
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Structure ─────────────────────────────────────────────────────────────────

function StructureTab({
  clubId,
  competitionId,
  competition,
  participants,
  onChange,
}: {
  clubId: string;
  competitionId: string;
  competition: SerializedCompetition;
  participants: AdminParticipant[];
  onChange: () => void;
}) {
  const { t } = useTranslation();
  const base = `/api/admin/clubs/${clubId}/competitions/${competitionId}`;
  const divisions =
    competition.divisions.length > 0 ? competition.divisions : [null];

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-500">
        {t("competitions.structureIntro")}
      </p>
      {divisions.map((label) => (
        <DivisionStructure
          key={label ?? "__single__"}
          base={base}
          label={label}
          format={competition.format}
          confirmedCount={
            participants.filter(
              (p) =>
                p.status === "CONFIRMED" &&
                (p.divisionLabel ?? null) === (label ?? null),
            ).length
          }
          onChange={onChange}
        />
      ))}
    </div>
  );
}

function DivisionStructure({
  base,
  label,
  format,
  confirmedCount,
  onChange,
}: {
  base: string;
  label: string | null;
  format: string;
  confirmedCount: number;
  onChange: () => void;
}) {
  const { t } = useTranslation();
  const [groupCount, setGroupCount] = useState("2");
  const [advancers, setAdvancers] = useState("2");

  const post = (path: string, body: unknown) =>
    apiPost(`${base}/${path}`, { divisionLabel: label, ...(body as object) });

  const autoGroups = useMutation({
    mutationFn: () => post("groups/auto", { groupCount: Number(groupCount) }),
    onSuccess: async () => {
      toast.success(t("common.saved"));
      onChange();
    },
    onError: (err) => toast.error(t(`errors.api.${getApiErrorCode(err)}`)),
  });
  const genRR = useMutation({
    mutationFn: () => post("fixtures/round-robin", { doubleRound: false }),
    onSuccess: async () => {
      toast.success(t("competitions.fixturesGenerated"));
      onChange();
    },
    onError: (err) => toast.error(t(`errors.api.${getApiErrorCode(err)}`)),
  });
  const genBracket = useMutation({
    mutationFn: () =>
      post("bracket", {
        source: format === "KNOCKOUT" ? "MANUAL" : "GROUPS",
        advancersPerGroup: Number(advancers),
        // MANUAL knockout seeds by current seed order on the server side when
        // seeds are omitted? No — send confirmed ids by seed. Server filters.
      }),
    onSuccess: async () => {
      toast.success(t("competitions.bracketGenerated"));
      onChange();
    },
    onError: (err) => toast.error(t(`errors.api.${getApiErrorCode(err)}`)),
  });

  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-4 space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="font-medium text-neutral-800">
          {label
            ? t("competitions.classNamed", { name: label })
            : t("competitions.mainDraw")}
        </h3>
        <Badge variant="secondary">
          {t("competitions.entrants", { count: confirmedCount })}
        </Badge>
      </div>

      {format !== "KNOCKOUT" && (
        <div className="flex items-end gap-2 flex-wrap">
          <div className="space-y-1">
            <Label className="text-[11px] text-neutral-500">
              {t("competitions.groupCountLabel")}
            </Label>
            <Input
              value={groupCount}
              onChange={(e) => setGroupCount(e.target.value)}
              type="number"
              min={1}
              className="h-9 w-20 rounded-lg border-neutral-200"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-9"
            disabled={autoGroups.isPending}
            onClick={() => autoGroups.mutate()}
          >
            {t("competitions.autoGroups")}
          </Button>
          <Button
            size="sm"
            className="h-9 bg-primary-600 hover:bg-primary-700 text-white"
            disabled={genRR.isPending}
            onClick={() => genRR.mutate()}
          >
            {t("competitions.generateRoundRobin")}
          </Button>
        </div>
      )}

      {format !== "LEAGUE" && (
        <div className="flex items-end gap-2 flex-wrap border-t border-neutral-100 pt-3">
          {format === "GROUPS_KNOCKOUT" && (
            <div className="space-y-1">
              <Label className="text-[11px] text-neutral-500">
                {t("competitions.advancersLabel")}
              </Label>
              <Input
                value={advancers}
                onChange={(e) => setAdvancers(e.target.value)}
                type="number"
                min={1}
                className="h-9 w-20 rounded-lg border-neutral-200"
              />
            </div>
          )}
          <Button
            size="sm"
            className="h-9 bg-primary-600 hover:bg-primary-700 text-white"
            disabled={genBracket.isPending}
            onClick={() => genBracket.mutate()}
          >
            {t("competitions.generateBracket")}
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Fixtures ──────────────────────────────────────────────────────────────────

function FixturesTab({
  clubId,
  competitionId,
  competition,
  onChange,
}: {
  clubId: string;
  competitionId: string;
  competition: SerializedCompetition;
  onChange: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<Fixture | null>(null);
  const base = `/api/admin/clubs/${clubId}/competitions/${competitionId}`;

  const allFixtures = competition.divisionsDetail.flatMap((d) => d.fixtures);
  if (allFixtures.length === 0) {
    return (
      <p className="text-sm text-neutral-400 py-6 text-center">
        {t("competitions.noFixtures")}
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {competition.divisionsDetail.map((d) =>
        d.fixtures.length === 0 ? null : (
          <div key={d.label ?? "__single__"}>
            {competition.divisions.length > 0 && (
              <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-2">
                {t("competitions.classNamed", { name: d.label })}
              </h3>
            )}
            <div className="space-y-2">
              {d.fixtures.map((f) => (
                <FixtureCard
                  key={f.id}
                  fixture={f}
                  onClick={() => f.home && f.away && setEditing(f)}
                />
              ))}
            </div>
          </div>
        ),
      )}
      {editing && (
        <ResultDialog
          base={base}
          fixture={editing}
          onClose={() => setEditing(null)}
          onSaved={onChange}
        />
      )}
    </div>
  );
}

export function fixtureContext(f: Fixture, t: TFunction) {
  if (f.phase === "KNOCKOUT") {
    if (f.isThirdPlace) return t("competitions.round.THIRD_PLACE");
    return t(`competitions.round.R${f.round}`, {
      defaultValue: t("competitions.roundN", { n: f.round }),
    });
  }
  const parts: string[] = [];
  if (f.groupName)
    parts.push(t("competitions.groupNamed", { name: f.groupName }));
  if (f.round) parts.push(t("competitions.roundN", { n: f.round }));
  return parts.join(" · ");
}

function FixtureCard({
  fixture: f,
  onClick,
}: {
  fixture: Fixture;
  onClick?: () => void;
}) {
  const { t } = useTranslation();
  const played = f.status === "COMPLETED";
  const homeWin = f.winnerSide === 0;
  const awayWin = f.winnerSide === 1;
  const sideName = (s: Fixture["home"]) => (s ? s.name : t("competitions.tbd"));

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick || !f.home || !f.away}
      className="w-full text-left bg-white rounded-lg border border-neutral-200 p-3 hover:border-primary-300 disabled:hover:border-neutral-200 transition-colors"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-neutral-400">
          {fixtureContext(f, t)}
        </span>
        {played ? (
          <Badge variant="secondary" className="text-[10px]">
            {f.confirmationStatus === "PENDING"
              ? t("competitions.awaitingConfirm")
              : f.scoreSummary || t("competitions.played")}
          </Badge>
        ) : (
          <span className="text-[11px] text-neutral-300">
            {t("competitions.notPlayed")}
          </span>
        )}
      </div>
      <div className="mt-1 space-y-0.5">
        <p
          className={`text-sm ${homeWin ? "font-semibold text-neutral-900" : "text-neutral-600"}`}
        >
          {sideName(f.home)}
        </p>
        <p
          className={`text-sm ${awayWin ? "font-semibold text-neutral-900" : "text-neutral-600"}`}
        >
          {sideName(f.away)}
        </p>
      </div>
    </button>
  );
}

function ResultDialog({
  base,
  fixture,
  onClose,
  onSaved,
}: {
  base: string;
  fixture: Fixture;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [sets, setSets] = useState<{ a: string; b: string }[]>(
    fixture.sets.length > 0
      ? fixture.sets.map((s) => ({ a: String(s.a), b: String(s.b) }))
      : [{ a: "", b: "" }],
  );
  const [winner, setWinner] = useState<"0" | "1" | "draw">(
    fixture.winnerSide === 1 ? "1" : fixture.winnerSide === 0 ? "0" : "0",
  );
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const cleanSets = sets
        .map((s) => ({ a: Number(s.a), b: Number(s.b) }))
        .filter(
          (s) =>
            Number.isFinite(s.a) &&
            Number.isFinite(s.b) &&
            (s.a > 0 || s.b > 0),
        );
      return apiPut(`${base}/fixtures/${fixture.id}/result`, {
        sets: cleanSets.length ? cleanSets : undefined,
        winnerSide: winner === "draw" ? null : Number(winner),
      });
    },
    onSuccess: () => {
      toast.success(t("common.saved"));
      onSaved();
      onClose();
    },
    onError: (err) => setErrorCode(getApiErrorCode(err)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("competitions.enterResult")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {errorCode && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t(`errors.api.${errorCode}`)}
              </AlertDescription>
            </Alert>
          )}
          <div className="grid grid-cols-[1fr_auto] gap-2 text-sm">
            <span className="font-medium text-neutral-700">
              {fixture.home?.name}
            </span>
            <span className="text-neutral-400">{t("competitions.home")}</span>
            <span className="font-medium text-neutral-700">
              {fixture.away?.name}
            </span>
            <span className="text-neutral-400">{t("competitions.away")}</span>
          </div>

          <div className="space-y-2">
            <Label className="text-sm text-neutral-600">
              {t("competitions.setsLabel")}
            </Label>
            {sets.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={s.a}
                  onChange={(e) =>
                    setSets((prev) =>
                      prev.map((x, j) =>
                        j === i ? { ...x, a: e.target.value } : x,
                      ),
                    )
                  }
                  type="number"
                  min={0}
                  className="h-9 w-16 rounded-lg border-neutral-200"
                />
                <span className="text-neutral-400">:</span>
                <Input
                  value={s.b}
                  onChange={(e) =>
                    setSets((prev) =>
                      prev.map((x, j) =>
                        j === i ? { ...x, b: e.target.value } : x,
                      ),
                    )
                  }
                  type="number"
                  min={0}
                  className="h-9 w-16 rounded-lg border-neutral-200"
                />
              </div>
            ))}
            {sets.length < 5 && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => setSets((p) => [...p, { a: "", b: "" }])}
              >
                <Plus className="w-3 h-3 mr-1" />
                {t("competitions.addSet")}
              </Button>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm text-neutral-600">
              {t("competitions.winnerLabel")}
            </Label>
            <Select
              value={winner}
              onValueChange={(v) => setWinner(v as "0" | "1" | "draw")}
            >
              <SelectTrigger className="rounded-lg border-neutral-200 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">{fixture.home?.name}</SelectItem>
                <SelectItem value="1">{fixture.away?.name}</SelectItem>
                <SelectItem value="draw">{t("competitions.draw")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              {mutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("common.save")
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Standings ─────────────────────────────────────────────────────────────────

function StandingsTab({ competition }: { competition: SerializedCompetition }) {
  const { t } = useTranslation();
  const [divIdx, setDivIdx] = useState(0);
  const divisions = competition.divisionsDetail;
  const division = divisions[divIdx] ?? divisions[0];

  if (!division) {
    return (
      <p className="text-sm text-neutral-400 py-6 text-center">
        {t("competitions.noStandings")}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {competition.divisions.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {divisions.map((d, i) => (
            <Button
              key={d.label ?? i}
              size="sm"
              variant={i === divIdx ? "default" : "outline"}
              className={i === divIdx ? "bg-primary-600 text-white" : ""}
              onClick={() => setDivIdx(i)}
            >
              {d.label ?? t("competitions.mainDraw")}
            </Button>
          ))}
        </div>
      )}

      {division.groups.map((g) => (
        <div key={g.name ?? "__single__"}>
          {g.name && (
            <h3 className="text-sm font-semibold text-neutral-700 mb-1">
              {t("competitions.groupNamed", { name: g.name })}
            </h3>
          )}
          <StandingsTable rows={g.standings} />
        </div>
      ))}

      {division.bracket && <BracketView bracket={division.bracket} />}
    </div>
  );
}

export function StandingsTable({
  rows,
}: {
  rows: SerializedCompetition["divisionsDetail"][number]["groups"][number]["standings"];
}) {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return (
      <p className="text-sm text-neutral-400 py-2">
        {t("competitions.noStandings")}
      </p>
    );
  }
  return (
    <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8">#</TableHead>
            <TableHead>{t("competitions.player")}</TableHead>
            <TableHead className="text-center w-10">
              {t("competitions.colP")}
            </TableHead>
            <TableHead className="text-center w-10">
              {t("competitions.colW")}
            </TableHead>
            <TableHead className="text-center w-10">
              {t("competitions.colL")}
            </TableHead>
            <TableHead className="text-center w-12">
              {t("competitions.colSets")}
            </TableHead>
            <TableHead className="text-center w-12 font-semibold">
              {t("competitions.colPts")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.participantId}>
              <TableCell className="text-neutral-400">{r.position}</TableCell>
              <TableCell className="font-medium">{r.name}</TableCell>
              <TableCell className="text-center">{r.played}</TableCell>
              <TableCell className="text-center">{r.won}</TableCell>
              <TableCell className="text-center">{r.lost}</TableCell>
              <TableCell className="text-center text-neutral-500">
                {r.setsFor}-{r.setsAgainst}
              </TableCell>
              <TableCell className="text-center font-semibold">
                {r.points}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function BracketView({
  bracket,
}: {
  bracket: NonNullable<
    SerializedCompetition["divisionsDetail"][number]["bracket"]
  >;
}) {
  const { t } = useTranslation();
  const sideName = (s: Fixture["home"]) => (s ? s.name : t("competitions.tbd"));
  return (
    <div className="space-y-4">
      <div className="flex gap-4 overflow-x-auto pb-2">
        {bracket.rounds.map((round) => (
          <div key={round.round} className="min-w-[170px] space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-400">
              {t(`competitions.round.${round.roundCode}`, {
                defaultValue: t("competitions.roundN", { n: round.round }),
              })}
            </h4>
            {round.matches.map((m) => (
              <div
                key={m.id}
                className="bg-white rounded-lg border border-neutral-200 p-2 text-sm"
              >
                <p
                  className={
                    m.winnerSide === 0
                      ? "font-semibold text-neutral-900"
                      : "text-neutral-500"
                  }
                >
                  {sideName(m.home)}
                </p>
                <p
                  className={
                    m.winnerSide === 1
                      ? "font-semibold text-neutral-900"
                      : "text-neutral-500"
                  }
                >
                  {sideName(m.away)}
                </p>
                {m.scoreSummary && (
                  <p className="text-[11px] text-neutral-400 mt-0.5">
                    {m.scoreSummary}
                  </p>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
      {bracket.thirdPlace && (
        <div className="min-w-[170px]">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-1">
            {t("competitions.round.THIRD_PLACE")}
          </h4>
          <div className="bg-white rounded-lg border border-neutral-200 p-2 text-sm max-w-[200px]">
            <p
              className={
                bracket.thirdPlace.winnerSide === 0
                  ? "font-semibold text-neutral-900"
                  : "text-neutral-500"
              }
            >
              {sideName(bracket.thirdPlace.home)}
            </p>
            <p
              className={
                bracket.thirdPlace.winnerSide === 1
                  ? "font-semibold text-neutral-900"
                  : "text-neutral-500"
              }
            >
              {sideName(bracket.thirdPlace.away)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
